import createReducer from 'utils/createReducer';
import {ParkingStatus} from '../../utils/bookingCard';

const actionTypes = {
  init: 'init',
  setCalendarEvents: 'setDeskBookingData',
  setAvailableDates: 'setAvailableDates',
  setCalendarEventsAvailableDays: 'setDeskbookingAvailableData',
  setSelectedDates: 'setSelectedDates',
  setBuilding: 'setBuilding',
  setFacility: 'setFacility',
  togglePositionId: 'togglePositionId',
  setBookingDetails: 'setBookingDetails',
  toggleSelectedDate: 'toggleSelectedDate',
  onSelectParking: 'onSelectParking',
  onSetParkingPlate: 'onSetParkingPlate',
  setLoading: 'setLoading'
};

const initialState = {
  bookedDates: [],
  bookedPosition: null,
  bookedParking: [],
  selectedDates: [],
  selectedBuilding: '',
  selectedFacility: '',
  selectedFacilityId: null,
  selectedPositionIds: [],
  selectedPosition: null,
  selectedParking: [],
  datesAndType: [],
  loading: false
};

const bookingEventTypes = {
  annualLeave: 'ANNUAL_LEAVE',
  holiday: 'HOLIDAY',
  deskBooking: 'DESK_BOOKING'
};

const deskBookingReducer = createReducer(initialState, {
  [actionTypes.init]: () => initialState,
  [actionTypes.setSelectedDates]: (state, payload) => {
    const selectedDates = payload.sort();
    const isSamedate = selectedDates?.[0] === state.bookedDates?.[0];

    return {
      ...state,
      selectedDates,
      selectedParking: selectedDates.map((date) => ({
        date,
        checked: isSamedate && state.bookedPosition ? state.bookedParking[0].checked : false,
        externalParkingChecked:
          isSamedate && state.bookedPosition
            ? state.bookedParking[0].externalParkingChecked
            : false,
        plateNumber: isSamedate && state.bookedPosition ? state.bookedParking[0].plateNumber : '',
        parkingStatus:
          isSamedate && state.bookedPosition ? state.bookedParking[0].parkingStatus : '',
        externalParkingSupported:
          isSamedate && state.bookedPosition
            ? state.bookedParking[0].externalParkingSupported
            : false
      })),
      selectedPositionIds: isSamedate && state.bookedPosition ? [state.bookedPosition.id] : [],
      selectedPosition: isSamedate && state.bookedPosition ? state.bookedPosition : null
    };
  },
  [actionTypes.setLoading]: (state, payload) => ({
    ...state,
    loading: payload
  }),
  [actionTypes.setBuilding]: (state, {building}) => ({
    ...state,
    selectedBuilding: building.label
  }),
  [actionTypes.setFacility]: (state, {floorId, building}) => ({
    ...state,
    selectedFacilityId: floorId,
    selectedFacility: building
  }),
  [actionTypes.togglePositionId]: (state, payload) => {
    const selectedPositionIds = updateSingleDeskId(state, payload.id);

    return {
      ...state,
      selectedPositionIds: selectedPositionIds,
      selectedPosition: selectedPositionIds.length ? payload : null
    };
  },
  [actionTypes.setBookingDetails]: (state, payload) => ({
    ...state,
    ...setDeskBookingSelection(state, payload)
  }),
  [actionTypes.toggleSelectedDate]: (state, isoDate) => ({
    ...state,
    selectedDates: updateSingleDate(state, isoDate)
  }),
  [actionTypes.onSelectParking]: (state, {date, field}) => ({
    ...state,
    selectedParking: state.selectedParking.map((parking) =>
      Object.assign(
        {
          ...parking
        },
        parking.date === date && field
      )
    )
  }),
  [actionTypes.onSetParkingPlate]: (state, plateNumber) => ({
    ...state,
    selectedParking: state.selectedParking.map((parking) => ({
      ...parking,
      plateNumber
    }))
  }),
  [actionTypes.SELECT_TYPE]: (state, payload) => {
    if (payload.isEdit) {
      delete payload.isEdit;
      return {
        ...state,
        datesAndType: [payload]
      };
    }
    delete payload.isEdit;
    const newDatesAndTypes = [...state.datesAndType];
    const dateTypeIdx = newDatesAndTypes.findIndex((obj) => obj.date === payload.date);
    if (dateTypeIdx === -1) {
      newDatesAndTypes.push(payload);
    } else {
      newDatesAndTypes[dateTypeIdx] = payload;
    }
    return {
      ...state,
      datesAndType: newDatesAndTypes.sort((a, b) => {
        const dateA = new Date(a.date);
        const dateB = new Date(b.date);

        return dateA - dateB;
      })
    };
  }
});

function updateSingleDeskId(state, id) {
  const dayIdx = state.selectedPositionIds.findIndex((deskId) => deskId === id);
  if (dayIdx === -1) return [id];

  return [];
}

function updateSingleDate(state, day) {
  const dayIdx = state.selectedDates.findIndex((sDay) => sDay === day);
  if (dayIdx === -1) return [day];

  return [];
}

function setDeskBookingSelection(state, payload) {
  const {
    date,
    facilityCity,
    facilitySectorName,
    positionId,
    facilityId,
    positionX,
    positionY,
    code,
    parkingSupported,
    externalParkingSupported,
    plateNumber,
    parkingStatus,
    parkingType,
    datesAndType
  } = payload;

  return {
    selectedDates: [date],
    selectedBuilding: `${facilityCity} - ${facilitySectorName}`,
    selectedFacilityId: facilityId,
    selectedPositionIds: [positionId],
    bookedDates: [date],
    bookedBuilding: `${facilityCity} - ${facilitySectorName}`,
    bookedParking: [date].map((date) => ({
      date,
      checked:
        (parkingSupported &&
          !externalParkingSupported &&
          (parkingStatus === ParkingStatus.ASSIGNED ||
            parkingStatus === ParkingStatus.REQUESTED)) ||
        (parkingSupported && externalParkingSupported && parkingType === 'Internal') ||
        (parkingSupported && externalParkingSupported && parkingType === 'All'),
      externalParkingChecked:
        ((externalParkingSupported && parkingType === 'External') ||
          (externalParkingSupported && parkingType === 'All')) ??
        false,
      plateNumber: plateNumber ?? '',
      parkingStatus,
      externalParkingSupported,
      parkingType,
      selectedFacility: facilityId
    })),
    bookedPosition: {
      facilityId,
      id: positionId,
      x: positionX,
      y: positionY,
      code
    },
    selectedPosition: {
      facilityId,
      id: positionId,
      x: positionX,
      y: positionY,
      code
    },
    datesAndType: [{...datesAndType, date}]
  };
}

export {actionTypes, deskBookingReducer, initialState, bookingEventTypes};
